/**
 * Convert array-based block conditions to Formly hideExpression
 * Handles: mutable-forms block
 */

export interface ArrayConditionProps {
	mode: 'equal' | 'below' | 'above' | 'between' | 'defined' | 'undefined';
	value?: number;
	to?: number;
}

/**
 * Convert a mutable-forms (array length) condition to hideExpression
 * Returns expression that evaluates to TRUE when condition is MET
 * (Remember: hideExpression hides when TRUE, so we'll invert later)
 *
 * Note: Mutable-forms stores an array, and conditions check the array length.
 * In Formly, we need to check if the field is an array and compare its length.
 */
export function convertArrayCondition(fieldKey: string, props: ArrayConditionProps): string | undefined {
	const { mode, value, to } = props;

	switch (mode) {
		case 'equal':
			// Array length must equal the specified number
			if (value === undefined || value === null) {
				return undefined;
			}
			return `(Array.isArray(model['${fieldKey}']) && model['${fieldKey}'].length === ${value})`;

		case 'below':
			// Array length must be less than the specified number
			if (value === undefined || value === null) {
				return undefined;
			}
			return `(Array.isArray(model['${fieldKey}']) && model['${fieldKey}'].length < ${value})`;

		case 'above':
			// Array length must be greater than the specified number
			if (value === undefined || value === null) {
				return undefined;
			}
			return `(Array.isArray(model['${fieldKey}']) && model['${fieldKey}'].length > ${value})`;

		case 'between':
			// Array length must be between two numbers (inclusive)
			if (value === undefined || value === null || to === undefined || to === null) {
				return undefined;
			}
			return `(Array.isArray(model['${fieldKey}']) && model['${fieldKey}'].length >= ${value} && model['${fieldKey}'].length <= ${to})`;

		case 'defined':
			// Field must have an array with at least one item
			return `(Array.isArray(model['${fieldKey}']) && model['${fieldKey}'].length > 0)`;

		case 'undefined':
			// Field must be empty, not an array, or an empty array
			return `(!model['${fieldKey}'] || !Array.isArray(model['${fieldKey}']) || model['${fieldKey}'].length === 0)`;

		default:
			// Unknown mode
			return undefined;
	}
}
